// pages/components/support/MyTickets.jsx
import React, { useState, useEffect } from 'react';
import { Loader2, Search, Filter, Clock, CheckCircle, AlertCircle, XCircle } from 'lucide-react';
import apiRequest from '@/services/requests';

const MyTickets = ({ onTicketClick, onRefresh }) => {
const [tickets, setTickets] = useState([]);
const [loading, setLoading] = useState(true);
const [page, setPage] = useState(1);
const [totalPages, setTotalPages] = useState(1);
const [totalResults, setTotalResults] = useState(0);
const [filters, setFilters] = useState({
status: '',
priority: ''
});

useEffect(() => {
loadTickets();
}, [page, filters]);

const loadTickets = async () => {
setLoading(true);
try {
const params = { page, limit: 10, ...filters };
// Remove empty filters
Object.keys(params).forEach(key => {
if (params[key] === '') delete params[key];
});

const res = await apiRequest.getSupportTickets(params);
if (res.success) {
setTickets(res.data);
setTotalPages(res.pagination.totalPages);
setTotalResults(res.pagination.totalResults);
}
} catch (error) {
console.error('Error loading tickets:', error);
}
setLoading(false);
};

const handleFilterChange = (filterType, value) => {
setFilters(prev => ({ ...prev, [filterType]: value }));
setPage(1);
};

const handleClearFilters = () => {
setFilters({ status: '', priority: '' });
setPage(1);
};

const getStatusBadge = (status) => {
const configs = {
open: {
icon: Clock,
bg: 'bg-green-100',
text: 'text-green-700',
border: 'border-green-200',
label: 'Open'
},
'in-progress': {
icon: Clock,
bg: 'bg-blue-100',
text: 'text-blue-700',
border: 'border-blue-200',
label: 'In Progress'
},
resolved: {
icon: CheckCircle,
bg: 'bg-purple-100',
text: 'text-purple-700',
border: 'border-purple-200',
label: 'Resolved'
},
closed: {
icon: XCircle,
bg: 'bg-slate-100',
text: 'text-slate-700',
border: 'border-slate-200',
label: 'Closed'
}
};

const config = configs[status] || configs.open;
const Icon = config.icon;

return (
<div className={`flex items-center gap-1.5 px-3 py-1 ${config.bg} ${config.text} border ${config.border} rounded-full text-xs font-semibold`}>
<Icon size={14} />
{config.label}
</div>
);
};

const getPriorityBadge = (priority) => {
const configs = {
low: { bg: 'bg-slate-100', text: 'text-slate-700' },
medium: { bg: 'bg-blue-100', text: 'text-blue-700' },
high: { bg: 'bg-amber-100', text: 'text-amber-700' },
urgent: { bg: 'bg-red-100', text: 'text-red-700' }
};

const config = configs[priority] || configs.medium;

return (
<span className={`px-2 py-1 ${config.bg} ${config.text} rounded text-xs font-medium`}>
{priority.charAt(0).toUpperCase() + priority.slice(1)}
</span>
);
};

const formatDate = (date) => {
return new Date(date).toLocaleDateString('en-US', {
year: 'numeric',
month: 'short',
day: 'numeric',
hour: '2-digit',
minute: '2-digit'
});
};

const hasActiveFilters = filters.status || filters.priority;

return (
<div className="space-y-6">
{/* Header & Filters */}
<div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
<div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
<div>
<h2 className="text-2xl font-bold text-slate-900">My Tickets</h2>
<p className="text-slate-600 mt-1">View and track your support requests</p>
</div>

<button
onClick={() => {
loadTickets();
if (onRefresh) onRefresh();
}}
className="px-4 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 font-medium rounded-lg transition-all"
>
Refresh
</button>
</div>

{/* Filters */}
<div className="flex flex-col md:flex-row gap-3">
<div className="flex-1">
<label className="block text-xs font-semibold text-slate-600 mb-1.5">Status</label>
<select
value={filters.status}
onChange={(e) => handleFilterChange('status', e.target.value)}
className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
>
<option value="">All Statuses</option>
<option value="open">Open</option>
<option value="in-progress">In Progress</option>
<option value="resolved">Resolved</option>
<option value="closed">Closed</option>
</select>
</div>

<div className="flex-1">
<label className="block text-xs font-semibold text-slate-600 mb-1.5">Priority</label>
<select
value={filters.priority}
onChange={(e) => handleFilterChange('priority', e.target.value)}
className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
>
<option value="">All Priorities</option>
<option value="low">Low</option>
<option value="medium">Medium</option>
<option value="high">High</option>
<option value="urgent">Urgent</option>
</select>
</div>

{hasActiveFilters && (
<div className="flex items-end">
<button
onClick={handleClearFilters}
className="px-4 py-2 text-slate-600 hover:text-slate-900 font-medium"
>
Clear Filters
</button>
</div>
)}
</div>
</div>

{/* Tickets List */}
{loading ? (
<div className="flex items-center justify-center py-12 bg-white rounded-xl border border-slate-200">
<Loader2 className="animate-spin text-blue-600" size={32} />
</div>
) : tickets.length === 0 ? (
<div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
<Search className="mx-auto text-slate-300 mb-4" size={48} />
<h3 className="text-lg font-semibold text-slate-900 mb-2">No Tickets Found</h3>
<p className="text-slate-600">
{hasActiveFilters
? 'Try adjusting your filters to see more results'
: "You haven't created any support tickets yet"}
</p>
</div>
) : (
<div className="space-y-3">
{tickets.map(ticket => (
<button
key={ticket.id}
onClick={() => onTicketClick(ticket)}
className="w-full bg-white rounded-xl border border-slate-200 p-6 hover:shadow-lg hover:border-blue-300 transition-all text-left group"
>
<div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
<div className="flex-1 min-w-0">
<div className="flex items-center gap-3 mb-2">
<h3 className="text-lg font-bold text-slate-900 group-hover:text-blue-600 transition-colors">
{ticket.subject}
</h3>
{getStatusBadge(ticket.status)}
</div>

<p className="text-sm text-slate-600 line-clamp-2 mb-3">
{ticket.message}
</p>

<div className="flex items-center gap-4 text-xs text-slate-500">
<span className="font-mono bg-slate-100 px-2 py-1 rounded">
{ticket.ticketId}
</span>
<span>•</span>
<span>{formatDate(ticket.createdAt)}</span>
<span>•</span>
{getPriorityBadge(ticket.priority)}
</div>
</div>

<div className="flex items-center gap-2 text-blue-600 group-hover:text-blue-700 font-medium text-sm">
View Details
<svg className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
</svg>
</div>
</div>
</button>
))}
</div>
)}

{/* Pagination */}
{totalPages > 1 && (
<div className="bg-white rounded-xl border border-slate-200 px-6 py-4 flex items-center justify-between">
<p className="text-sm text-slate-600">
Showing {tickets.length} of {totalResults} tickets
</p>

<div className="flex items-center gap-2">
<button
onClick={() => setPage(p => Math.max(1, p - 1))}
disabled={page === 1}
className="px-4 py-2 bg-white border border-slate-300 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
>
Previous
</button>

<span className="text-sm text-slate-600 px-4">
Page {page} of {totalPages}
</span>

<button
onClick={() => setPage(p => Math.min(totalPages, p + 1))}
disabled={page === totalPages}
className="px-4 py-2 bg-white border border-slate-300 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
>
Next
</button>
</div>
</div>
)}
</div>
);
};

export default MyTickets;